import React from 'react';
import { motion } from 'framer-motion';
import { LinkIcon, CreditCardIcon, LayoutDashboardIcon, BuildingIcon, RefreshCwIcon, ArrowRightIcon, CheckCircle2Icon, ClockIcon, AlertCircleIcon, SparklesIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
interface DemoLandingScreenProps {
  onStartDataSharingJourney: () => void;
  onStartVRPJourney: () => void;
  onViewTPPCMI: () => void;
  onViewLFICMI: () => void;
  onResetDemo: () => void;
  onSkipToApp?: () => void;
  activeCount: number;
  pendingCount: number;
  historyCount: number;
}
interface JourneyCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  buttonText: string;
  onClick: () => void;
  delay: number;
  accentColor: string;
  gradient: string;
}
function JourneyCard({
  icon,
  title,
  description,
  features,
  buttonText,
  onClick,
  delay,
  accentColor,
  gradient
}: JourneyCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.4
  }} whileHover={{
    y: -4,
    transition: {
      duration: 0.2
    }
  }} className="bg-white border-2 overflow-hidden group cursor-pointer" style={{
    borderColor: accentColor + '40',
    borderRadius: OPENFINANCE_THEME.radius.xl,
    boxShadow: `0 4px 20px ${accentColor}20`
  }} onClick={onClick} data-id="element-686">
      {/* Gradient Header */}
      <div className="h-24 relative overflow-hidden" style={{
      background: gradient
    }} data-id="element-687">
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-688">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }} data-id="element-689">
            <div className="text-white" data-id="element-690">{icon}</div>
          </div>
        </div>
      </div>

      <div className="p-6" data-id="element-691">
        {/* Title & Description */}
        <h3 className="text-xl font-bold mb-2" style={{
        color: OPENFINANCE_THEME.colors.text,
        fontFamily: OPENFINANCE_THEME.fonts.heading
      }} data-id="element-692">
          {title}
        </h3>
        <p className="text-sm mb-4 leading-relaxed" style={{
        color: OPENFINANCE_THEME.colors.textMuted,
        fontFamily: OPENFINANCE_THEME.fonts.body
      }} data-id="element-693">
          {description}
        </p>

        {/* Features */}
        <div className="space-y-2 mb-5" data-id="element-694">
          {features.map((feature, index) => <div key={index} className="flex items-start gap-2" data-id="element-695">
              <CheckCircle2Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
            color: accentColor
          }} data-id="element-696" />
              <span className="text-xs" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-697">
                {feature}
              </span>
            </div>)}
        </div>

        {/* Button */}
        <button className="w-full px-4 py-3 font-semibold text-white flex items-center justify-center gap-2 transition-all group-hover:shadow-lg" style={{
        background: gradient,
        borderRadius: OPENFINANCE_THEME.radius.md
      }} data-id="element-698">
          <span data-id="element-699">{buttonText}</span>
          <motion.div animate={{
          x: [0, 4, 0]
        }} transition={{
          repeat: Infinity,
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-700">
            <ArrowRightIcon className="w-4 h-4" data-id="element-701" />
          </motion.div>
        </button>
      </div>
    </motion.div>;
}
interface CMICardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  stats: {
    label: string;
    value: number;
    color: string;
  }[];
  buttonText: string;
  onClick: () => void;
  delay: number;
  accentColor: string;
}
function CMICard({
  icon,
  title,
  description,
  stats,
  buttonText,
  onClick,
  delay,
  accentColor
}: CMICardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.4
  }} whileHover={{
    y: -2,
    transition: {
      duration: 0.2
    }
  }} className="bg-white border-2 overflow-hidden group cursor-pointer" style={{
    borderColor: OPENFINANCE_THEME.colors.border,
    borderRadius: OPENFINANCE_THEME.radius.xl
  }} onClick={onClick} data-id="element-702">
      <div className="p-5" data-id="element-703">
        {/* Icon & Title */}
        <div className="flex items-start gap-3 mb-3" data-id="element-704">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: accentColor + '15'
        }} data-id="element-705">
            <div style={{
            color: accentColor
          }} data-id="element-706">
              {icon}
            </div>
          </div>
          <div className="flex-1" data-id="element-707">
            <h4 className="text-base font-bold mb-1" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-708">
              {title}
            </h4>
            <p className="text-xs" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-709">
              {description}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 mb-4" data-id="element-710">
          {stats.map((stat, index) => <div key={index} data-id="element-711">
              <div className="text-xs font-medium mb-1" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-712">
                {stat.label}
              </div>
              <div className="text-xl font-bold" style={{
            color: stat.color
          }} data-id="element-713">
                {stat.value}
              </div>
            </div>)}
        </div>

        {/* Button */}
        <button className="w-full px-3 py-2 text-sm font-medium border-2 transition-colors hover:bg-gray-50" style={{
        borderColor: OPENFINANCE_THEME.colors.border,
        color: OPENFINANCE_THEME.colors.text,
        borderRadius: OPENFINANCE_THEME.radius.md
      }} data-id="element-714">
          {buttonText}
        </button>
      </div>
    </motion.div>;
}
export function DemoLandingScreen({
  onStartDataSharingJourney,
  onStartVRPJourney,
  onViewTPPCMI,
  onViewLFICMI,
  onResetDemo,
  onSkipToApp,
  activeCount,
  pendingCount,
  historyCount
}: DemoLandingScreenProps) {
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-715">
      <div className="max-w-6xl mx-auto px-4 py-12" data-id="element-716">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center mb-12" data-id="element-717">
          {/* VeloX Branding */}
          <div className="flex justify-center mb-6" data-id="element-718">
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="relative" data-id="element-719">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center p-4 relative overflow-hidden" style={{
              background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`,
              boxShadow: `0 8px 24px ${OPENFINANCE_THEME.colors.primary500}40`
            }} data-id="element-720">
                <img src="https://cdn.magicpatterns.com/uploads/6ixnVN3mm3sFs3kb5m4Wow/apple-touch-icon.png" alt="VeloX" className="w-full h-full object-contain" data-id="element-721" />
                <motion.div className="absolute top-1 right-1" animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                repeat: Infinity,
                duration: 2
              }} data-id="element-722">
                  <SparklesIcon className="w-4 h-4 text-yellow-300" data-id="element-723" />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{
          background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary600} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontFamily: OPENFINANCE_THEME.fonts.heading
        }} data-id="element-724">
            VeloX Open Finance Demo
          </h1>

          {/* Subtitle */}
          <p className="text-lg mb-2" style={{
          color: OPENFINANCE_THEME.colors.text,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-725">
            UAE Consent Management Interface
          </p>
          <p className="text-sm max-w-2xl mx-auto" style={{
          color: OPENFINANCE_THEME.colors.textMuted,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-726">
            Experience complete Open Finance journeys from data sharing to bill
            payments
          </p>
        </motion.div>

        {/* User Journeys Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="mb-8" data-id="element-727">
          <div className="text-center mb-6" data-id="element-728">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary500 + '15',
            border: `2px solid ${OPENFINANCE_THEME.colors.primary500}30`
          }} data-id="element-729">
              <ZapIcon className="w-4 h-4" style={{
              color: OPENFINANCE_THEME.colors.primary500
            }} data-id="element-730" />
              <span className="text-sm font-semibold" style={{
              color: OPENFINANCE_THEME.colors.primary600
            }} data-id="element-731">
                Experience User Journeys
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-732">
              Choose Your Demo Path
            </h2>
            <p className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-733">
              See how users interact with VeloX's Open Finance features
            </p>
          </div>

          {/* Journey Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-734">
            {/* Data Sharing Journey */}
            <JourneyCard icon={<LinkIcon className="w-8 h-8" data-id="element-736" />} title="Data Sharing Journey" description="Experience how users securely connect their bank accounts to VeloX and share financial data" features={['Select your bank from UAE providers', 'Authenticate with Face ID', 'Authorize account access', 'View connected dashboard']} buttonText="Start Data Sharing" onClick={onStartDataSharingJourney} delay={0.4} accentColor={OPENFINANCE_THEME.colors.primary500} gradient={`linear-gradient(135deg, ${OPENFINANCE_THEME.colors.primary500} 0%, ${OPENFINANCE_THEME.colors.primary600} 100%)`} data-id="element-735" />

            {/* VRP Journey */}
            <JourneyCard icon={<CreditCardIcon className="w-8 h-8" data-id="element-738" />} title="Bill Payment Journey" description="See how users set up automated recurring payments for their bills with Variable Recurring Payments" features={['Configure payment rules & limits', 'Review and authorize VRP consent', 'Bank authentication', 'Auto-pay enabled confirmation']} buttonText="Start Bill Payment" onClick={onStartVRPJourney} delay={0.5} accentColor={OPENFINANCE_THEME.colors.accent500} gradient={`linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`} data-id="element-737" />
          </div>

          {/* Skip to App Link */}
          {onSkipToApp && <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.3
        }} onClick={onSkipToApp} className="w-full px-6 py-3 text-sm font-medium border-2 transition-all hover:shadow-md hover:bg-white flex items-center justify-center gap-2" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          color: OPENFINANCE_THEME.colors.textMuted,
          borderRadius: OPENFINANCE_THEME.radius.xl,
          backgroundColor: 'transparent'
        }} data-id="element-739">
              <LayoutDashboardIcon className="w-4 h-4" data-id="element-740" />
              <span data-id="element-741">Or skip to VeloX dashboard (with existing data)</span>
            </motion.button>}
        </motion.div>

        {/* CMI Management Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7,
        duration: 0.5
      }} className="mb-8" data-id="element-742">
          <div className="text-center mb-6" data-id="element-743">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3" style={{
            backgroundColor: OPENFINANCE_THEME.colors.accent500 + '15',
            border: `2px solid ${OPENFINANCE_THEME.colors.accent500}30`
          }} data-id="element-744">
              <ShieldCheckIcon className="w-4 h-4" style={{
              color: OPENFINANCE_THEME.colors.accent500
            }} data-id="element-745" />
              <span className="text-sm font-semibold" style={{
              color: OPENFINANCE_THEME.colors.accent600
            }} data-id="element-746">
                Consent Management Interfaces
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.heading
          }} data-id="element-747">
              Explore Management Views
            </h2>
            <p className="text-sm" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-748">
              Jump directly to consent management from either perspective
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-749">
            {/* TPP CMI */}
            <CMICard icon={<LayoutDashboardIcon className="w-5 h-5" data-id="element-751" />} title="TPP Consent Management" description="VeloX's view: Manage user consents and connections" stats={[{
            label: 'Active',
            value: activeCount,
            color: OPENFINANCE_THEME.colors.success
          }, {
            label: 'History',
            value: historyCount,
            color: OPENFINANCE_THEME.colors.textMuted
          }]} buttonText="Open TPP CMI" onClick={onViewTPPCMI} delay={0.8} accentColor={OPENFINANCE_THEME.colors.primary500} data-id="element-750" />

            {/* LFI CMI */}
            <CMICard icon={<BuildingIcon className="w-5 h-5" data-id="element-753" />} title="LFI Consent Management" description="Bank's view: Authorize and monitor TPP access" stats={[{
            label: 'Pending',
            value: pendingCount,
            color: OPENFINANCE_THEME.colors.warning
          }, {
            label: 'Active',
            value: activeCount,
            color: OPENFINANCE_THEME.colors.success
          }]} buttonText="Open LFI CMI" onClick={onViewLFICMI} delay={0.9} accentColor="#E31E24" data-id="element-752" />
          </div>
        </motion.div>

        {/* Info & Reset Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.0,
        duration: 0.4
      }} className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-754">
          {/* Features */}
          <div className="bg-white border-2 p-6" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          borderRadius: OPENFINANCE_THEME.radius.xl
        }} data-id="element-755">
            <div className="flex items-center gap-3 mb-4" data-id="element-756">
              <CheckCircle2Icon className="w-5 h-5" style={{
              color: OPENFINANCE_THEME.colors.success
            }} data-id="element-757" />
              <h3 className="text-lg font-bold" style={{
              color: OPENFINANCE_THEME.colors.text,
              fontFamily: OPENFINANCE_THEME.fonts.heading
            }} data-id="element-758">
                Demo Features
              </h3>
            </div>
            <ul className="space-y-2" data-id="element-759">
              {['Data sharing consent flow', 'VRP bill payment automation', 'Bank authentication (Face ID)', 'Dual perspective (TPP & LFI)', 'Pause, suspend, and revoke consents', 'UAE Open Finance Standards v2.1-rc2'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-sm" style={{
              color: OPENFINANCE_THEME.colors.textMuted,
              fontFamily: OPENFINANCE_THEME.fonts.body
            }} data-id="element-760">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{
                backgroundColor: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-761" />
                  <span data-id="element-762">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* Demo Data */}
          <div className="bg-white border-2 p-6" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          borderRadius: OPENFINANCE_THEME.radius.xl
        }} data-id="element-763">
            <div className="flex items-center gap-3 mb-4" data-id="element-764">
              <ClockIcon className="w-5 h-5" style={{
              color: OPENFINANCE_THEME.colors.accent500
            }} data-id="element-765" />
              <h3 className="text-lg font-bold" style={{
              color: OPENFINANCE_THEME.colors.text,
              fontFamily: OPENFINANCE_THEME.fonts.heading
            }} data-id="element-766">
                Demo Data
              </h3>
            </div>
            <div className="space-y-3 mb-4" data-id="element-767">
              <div className="flex items-center justify-between" data-id="element-768">
                <span className="text-sm" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-769">
                  Active Consents
                </span>
                <span className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.success
              }} data-id="element-770">
                  {activeCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-771">
                <span className="text-sm" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-772">
                  Pending Requests
                </span>
                <span className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.warning
              }} data-id="element-773">
                  {pendingCount}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-774">
                <span className="text-sm" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-775">
                  History
                </span>
                <span className="text-lg font-bold" style={{
                color: OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-776">
                  {historyCount}
                </span>
              </div>
            </div>
            <button onClick={onResetDemo} className="w-full px-4 py-2 text-sm font-medium border-2 transition-colors hover:bg-gray-50 flex items-center justify-center gap-2" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            color: OPENFINANCE_THEME.colors.text,
            borderRadius: OPENFINANCE_THEME.radius.md
          }} data-id="element-777">
              <RefreshCwIcon className="w-4 h-4" data-id="element-778" />
              <span data-id="element-779">Reset Demo Data</span>
            </button>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.1,
        duration: 0.4
      }} className="text-center" data-id="element-780">
          <div className="inline-flex items-center gap-2 px-4 py-2 border-2" style={{
          borderColor: OPENFINANCE_THEME.colors.borderLight,
          borderRadius: OPENFINANCE_THEME.radius.full,
          backgroundColor: 'white'
        }} data-id="element-781">
            <AlertCircleIcon className="w-4 h-4" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-782" />
            <span className="text-xs" style={{
            color: OPENFINANCE_THEME.colors.textMuted,
            fontFamily: OPENFINANCE_THEME.fonts.body
          }} data-id="element-783">
              This is a demonstration environment with mock data
            </span>
          </div>
        </motion.div>
      </div>
    </div>;
}